// @flow
import * as React from "react";
import _Select from "../HistoryPages/Select";
import styled, { css, ThemeConsumer } from "styled-components";
import Gettext from "../common/Gettext";
import _Flag from "../common/Flag";
import countryimg from "../../images/country.svg";
import { fontSize, rtl } from "../common/styled-mixins";

const Select = styled(_Select)`
  height: ${({ theme }) => (theme.newIndex ? "50px" : "40px")};
  min-width: 11.25em;
  font-weight: 400;
  font-size: ${fontSize(16)};
  line-height: 1;

  .react-select__menu {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .react-select__control {
    height: 100%;
    width: 100%;
    font-size: inherit;
    line-height: inherit;
    color: ${({ theme }) => theme.inputColor};
    border: 1px solid #cccccc;
    border-radius: 3px;
    background-color: #f6f6f6;
    box-shadow: inset 0px 2px 0px 0px #e3e3e3;

    :hover {
      box-shadow: inset 0px 2px 0px 0px #e3e3e3;
    }

    ${({ theme }) =>
      theme.newIndex &&
      css`
        min-height: 50px;
        background-color: rgba(255, 255, 255, 0.25);
        border-color: transparent;
        border-radius: 25px;
        box-shadow: none;

        :hover,
        :focus,
        :active {
          border-color: transparent;
          box-shadow: none;
        }

        @media (max-width: 959px) {
          min-height: 0;
        }

        ${({ menuIsOpen }) =>
          menuIsOpen &&
          css`
            background-color: rgba(255, 255, 255, 0.25) !important;
            border-color: #fff;
            border-radius: 25px 25px 0 0;

            :hover,
            :focus,
            :active {
              border-color: #fff;
            }
          `};
      `};
  }

  .react-select__value-container {
    height: 100%;
    align-content: center;
  }

  .react-select__dropdown-indicator {
    ${({ theme }) =>
      theme.newIndex &&
      css`
        color: #fff;
        opacity: 0.5;

        :hover {
          color: #fff;
          opacity: 1;
        }

        ${({ menuIsOpen }) =>
          menuIsOpen &&
          css`
            color: #fff;
            opacity: 1;
          `};
      `};
  }

  @media (max-width: 959px) {
    height: 40px;
    font-size: ${fontSize(14)};
  }
`;

const Flag = styled(_Flag)`
  ${rtl("margin-right", "17px")};
`;

const Currency = styled.span`
  ${rtl("margin-left", "6px")};
  font-weight: 600;
  font-size: ${fontSize(12)};
  color: ${({ theme, isOption }) =>
    isOption
      ? theme.mode === "light"
        ? "#1B546A"
        : "#959595"
      : theme.mode === "light"
      ? theme.authCurrencyCodeColor
      : "#959595"};
`;

const PlaceholderView = styled.div`
  display: flex;
  ${rtl("padding-left", "37px")};
  font-size: ${fontSize(16)};
  line-height: 20px;
  ${rtl("text-align", "left")};
  color: #757575;
  background: url(${countryimg}) no-repeat 2px 50%;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      ${rtl("padding-left", "10px")};
      color: #ffffff;
      background: none;
    `};
`;

const ValueOption = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-content: flex-start;
  align-items: center;
  text-align: left;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: #fff;
    `};
`;

const Option = styled(ValueOption)`
  padding: ${({ code }) => (code === "world" ? "4px 10px" : "8px 10px")};
  color: #343434;
  box-sizing: border-box;
  cursor: pointer;

  :hover {
    color: #fff;
    background-color: #3498db;

    ${Currency} {
      color: #fff;
    }
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: #e3e3e3;
      pointer-events: none;
    `};
`;

type Country = *;

type Props = {
  name: string,
  value: Country,
  options: Array<Country>,
  onChange: (target: Object) => void,
};

const Placeholder = () => (
  <PlaceholderView>
    <Gettext>Country/Region</Gettext>
  </PlaceholderView>
);

const CountryOption = ({ label, currency, code, isOption }) => (
  <React.Fragment>
    <Flag country={code} />
    {label}
    <Currency isOption={isOption}>({currency})</Currency>
  </React.Fragment>
);

type State = {
  menuIsOpen: boolean,
};

class CountrySelect extends React.Component<Props, State> {
  state = {
    menuIsOpen: false,
  };

  handleMenuEvent = (menuIsOpen: boolean) => {
    this.setState({ menuIsOpen });
  };

  handleChange = (value: *) => {
    this.props.onChange({
      target: {
        name: this.props.name,
        value: value,
      },
    });
  };

  render() {
    return (
      <ThemeConsumer>
        {(theme) => (
          <Select
            classNamePrefix="react-select"
            isRtl={theme.direction === "rtl"}
            value={this.props.value}
            options={this.props.options}
            menuIsOpen={this.state.menuIsOpen}
            onMenuOpen={() => this.handleMenuEvent(true)}
            onMenuClose={() => this.handleMenuEvent(false)}
            components={{
              SingleValue: ({ innerProps, data }) => (
                <ValueOption {...innerProps}>
                  <CountryOption {...data} />
                </ValueOption>
              ),
              Option: ({ innerProps, isSelected, data }) => (
                <Option
                  {...innerProps}
                  isSelected={isSelected}
                  code={data.code}
                >
                  <CountryOption {...data} isOption />
                </Option>
              ),
            }}
            onChange={this.handleChange}
            placeholder={<Placeholder />}
          />
        )}
      </ThemeConsumer>
    );
  }
}

export default CountrySelect;



// WEBPACK FOOTER //
// ./src/components/Auth/CountrySelect.js